import {
  Attributes,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  openDeleteDialog,
  openDialog,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {assoc, isNil} from 'ramda';
import {isArray, isNotNil, noop} from 'ramda-adjunct';

import {useGetVehicleV2Query, useRemoveVehicleWarrantyMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {DmsDataGrid, type DmsDataGridProps, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {parseDate, composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {VehicleWarrantyForm} from './VehicleWarrantyForm';

const WARRANTY_GRID_CODE = 'vehicle-warranty';
const WARRANTY_GRID_HEIGHT = 80;

type ModelSpecificationCardProps = Pick<DmsDataGridProps, 'maximization'> & {
  vehicleId: string;
  canEditVehicle: boolean;
};

function ModelSpecificationCard(props: ModelSpecificationCardProps) {
  const navigate = useNavigate();
  const {data: vehicle, isLoading, isError} = useGetVehicleV2Query({vehicleId: props.vehicleId});

  const formatDateTime = useDateTimeFormatter();

  const [removeVehicleWarranty] = useRemoveVehicleWarrantyMutation();

  const handleOnClick = () =>
    navigate(
      composePath(vehiclesRoutes.editV2Tab, {
        params: {id: props.vehicleId, tab: 'vehicle-data'},
      })
    );

  // Scope the warranty grid to the current vehicle; the Vehicle column is implicit here.
  const queryModifier = assoc('vehicleId', props.vehicleId);

  const refreshWarrantyGrid = () => refreshDmsDatagrid(WARRANTY_GRID_CODE);

  const openAssignWarrantyDialog = () =>
    openDialog(
      <VehicleWarrantyForm
        vehicleId={props.vehicleId}
        onDiscard={closeCurrentDialog}
        onSuccess={() => {
          closeCurrentDialog();
          refreshWarrantyGrid();
        }}
        data-testid={testIds.vehicles.vehicleData('assignWarrantyDialog')}
      />,
      {title: i18n.t('entity.vehicleWarranty.actions.assign')}
    );

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const warrantyProductId = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <VehicleWarrantyForm
            vehicleId={props.vehicleId}
            warrantyProductId={warrantyProductId}
            onDiscard={closeCurrentDialog}
            onSuccess={() => {
              closeCurrentDialog();
              refreshData();
            }}
            data-testid={testIds.vehicles.vehicleData('editWarrantyDialog')}
          />,
          {title: i18n.t('entity.vehicleWarranty.actions.edit')}
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            removeVehicleWarranty({vehicleId: props.vehicleId, warrantyProductId})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <Card
      title={i18n.t('entity.vehicle.labels.warrantyAndValidity')}
      actions={
        props.canEditVehicle
          ? [
              {
                variant: 'link',
                type: 'button',
                leftIcon: 'image/edit',
                onClick: handleOnClick,
                'data-testid': testIds.vehicles.vehicleData('warrantyAndValidity-edit'),
                title: i18n.t('general.actions.edit'),
              },
            ]
          : undefined
      }
      data-testid={testIds.vehicles.vehicleData('warrantyAndValidity')}
    >
      <VStack>
        <DataStatus isLoading={isLoading} isError={isError}>
          <Attributes
            data-testid={testIds.vehicles.vehicleData('warrantyAndValidity-parameters')}
            rows={[
              {
                label: i18n.t('entity.vehicle.labels.warrantyUntil'),
                value: isNotNil(vehicle?.warrantyDate)
                  ? formatDateTime('dateShort', parseDate(vehicle?.warrantyDate ?? ''))
                  : null,
                testId: 'warrantyUntil',
              },
              {
                label: i18n.t('entity.vehicle.labels.warrantyMileageLimit'),
                value: String(isNil(vehicle?.warrantyMileage) ? '' : vehicle?.warrantyMileage),
                testId: 'warrantyMileageLimit',
              },
              {
                label: i18n.t('entity.vehicle.labels.serviceContractUntil'),
                value: vehicle?.serviceContractUntil
                  ? formatDateTime('dateShort', parseDate(vehicle?.serviceContractUntil))
                  : null,
                testId: 'serviceContractUntil',
              },
              {
                label: i18n.t('entity.vehicle.labels.serviceContractMileageLimit'),
                value: vehicle?.serviceContractMileageLimit,
                testId: 'serviceContractMileageLimit',
              },
              {
                label: i18n.t('entity.vehicle.labels.serviceContractNote'),
                value: vehicle?.serviceContractNote,
                testId: 'serviceContractNote',
              },
            ]}
          />
        </DataStatus>
        <VStack spacing={2}>
          <Show when={props.canEditVehicle}>
            <HStack justify="flex-end">
              <Button
                variant="link"
                leftIcon="content/add_circle"
                title={i18n.t('entity.vehicleWarranty.actions.assign')}
                onClick={openAssignWarrantyDialog}
                data-testid={testIds.vehicles.vehicleData('assignWarranty')}
              />
            </HStack>
          </Show>
          <Box height={WARRANTY_GRID_HEIGHT}>
            <DmsDataGrid
              maximization={props.maximization}
              gridCode={WARRANTY_GRID_CODE}
              actionCallback={actionCallback}
              queryModifier={queryModifier}
              data-testid={testIds.vehicles.vehicleData('warranties')}
            />
          </Box>
        </VStack>
      </VStack>
    </Card>
  );
}

export const WarrantyCard = ModelSpecificationCard;
